import type { TFunction } from 'i18next';

import { formatMoneyBound } from './money-range';
import type { FilterDef, FilterOption, FilterValue } from './types';

export interface FilterSummary {
  /** "is", "is not", "contains", "is between", "is at least", "is at most". */
  verb: string;
  /** "Active, Draft", "“gold”", "$10 and $20". */
  detail: string;
  /** `${label} ${verb} ${detail}`: the chip's accessible name. */
  text: string;
}

/** A value's label: the enum option, a resolved async option, or the raw value. */
export function optionLabel(
  def: FilterDef<never>,
  value: string,
  resolved?: ReadonlyMap<string, FilterOption>,
): string {
  if (def.type === 'enum') {
    return def.options.find((option) => option.value === value)?.label ?? value;
  }
  return resolved?.get(value)?.label ?? value;
}

/** "Sep 28, 2026" for a `YYYY-MM-DD` key, with no timezone drift. */
export function formatDateKey(key: string, locale?: string): string {
  const date = new Date(`${key}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return key;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(date);
}

export interface FilterSummaryOptions {
  /** `common` namespace translator: the verbs and joiners are `indexTable.summary.*`. */
  t: TFunction;
  resolved?: ReadonlyMap<string, FilterOption>;
  locale?: string;
  /** Values listed before "+N more". Default 2, as Shopify's chips do. */
  maxListed?: number;
}

/** The human summary of an applied filter, for chips, tooltips and screen readers. */
export function filterSummary(
  def: FilterDef<never>,
  value: FilterValue,
  { t, resolved, locale, maxListed = 2 }: FilterSummaryOptions,
): FilterSummary {
  const done = (verb: string, detail: string): FilterSummary => {
    const translatedVerb = t(`indexTable.summary.verb.${verb}`);
    return {
      verb: translatedVerb,
      detail,
      text: t('indexTable.summary.text', { label: def.label, verb: translatedVerb, detail }),
    };
  };
  switch (value.op) {
    case 'in':
    case 'not_in': {
      const labels = value.values.map((v) => optionLabel(def, v, resolved));
      const shown = labels.slice(0, maxListed);
      const rest = labels.length - shown.length;
      const detail =
        rest > 0
          ? t('indexTable.summary.more', { list: shown.join(', '), count: rest })
          : shown.join(', ');
      return done(value.op === 'in' ? 'is' : 'isNot', detail);
    }
    case 'contains':
      return done('contains', t('indexTable.summary.quoted', { value: value.value }));
    case 'eq': {
      const yes = def.type === 'boolean' ? def.trueLabel : undefined;
      const no = def.type === 'boolean' ? def.falseLabel : undefined;
      return done(
        'is',
        value.value ? (yes ?? t('indexTable.boolean.yes')) : (no ?? t('indexTable.boolean.no')),
      );
    }
    case 'between': {
      const format = (v: string) => {
        if (def.type === 'date-range') return formatDateKey(v, locale);
        if (def.type === 'number-range') {
          if (def.currency) return formatMoneyBound(def.currency, v, locale);
          return `${def.prefix ?? ''}${v}${def.suffix ?? ''}`;
        }
        return v;
      };
      if (value.min && value.max) {
        return value.min === value.max
          ? done(def.type === 'date-range' ? 'isOn' : 'is', format(value.min))
          : done(
              'isBetween',
              t('indexTable.summary.range', { min: format(value.min), max: format(value.max) }),
            );
      }
      if (value.min) {
        return done(def.type === 'date-range' ? 'isOnOrAfter' : 'isAtLeast', format(value.min));
      }
      return done(def.type === 'date-range' ? 'isOnOrBefore' : 'isAtMost', format(value.max ?? ''));
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Date presets                                                               */
/* -------------------------------------------------------------------------- */

/** `YYYY-MM-DD` of a local calendar day. */
export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateKey(date);
}

/**
 * Shopify's date filter choices. Ranges end today, in the user's timezone.
 * Labels are `common:indexTable.datePresets.<id>`.
 */
export function defaultDatePresets(t: TFunction) {
  return [
    {
      id: 'today',
      label: t('indexTable.datePresets.today'),
      range: () => ({ from: daysAgo(0), to: daysAgo(0) }),
    },
    {
      id: 'last-7-days',
      label: t('indexTable.datePresets.last7Days'),
      range: () => ({ from: daysAgo(6), to: daysAgo(0) }),
    },
    {
      id: 'last-30-days',
      label: t('indexTable.datePresets.last30Days'),
      range: () => ({ from: daysAgo(29), to: daysAgo(0) }),
    },
    {
      id: 'last-90-days',
      label: t('indexTable.datePresets.last90Days'),
      range: () => ({ from: daysAgo(89), to: daysAgo(0) }),
    },
    {
      id: 'last-12-months',
      label: t('indexTable.datePresets.last12Months'),
      range: () => ({ from: daysAgo(364), to: daysAgo(0) }),
    },
  ];
}
